import { useMemo, useState } from "react";
import "./AdminOverview.css";
import { Button, Select, Table, Tags, ArrowBigRight } from "../../../ui";
import MetricsGrid from "../MetricsGrid/MetricsGrid";
import { CATEGORIES } from "../../utils/ludDocuments.constants";
import { buildAdminIssues } from "../../utils/ludDocuments.helpers";

export default function AdminOverview({ restaurants, lang, onBack, onJump }) {
  const ar = lang === "ar";
  const [restaurantFilter, setRestaurantFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const onClearFilters = () => {
  setRestaurantFilter("all");
  setCategoryFilter("all");
  setSeverityFilter("all");
};

  const allIssues = useMemo(() => buildAdminIssues(restaurants, lang), [restaurants, lang]);
  const issues = allIssues.filter((issue) =>
    (restaurantFilter === "all" || issue.restaurantId === restaurantFilter) &&
    (categoryFilter === "all" || issue.categoryKey === categoryFilter) &&
    (severityFilter === "all" || issue.severity === severityFilter),
  );

  const affectedRestaurants = new Set(allIssues.map((issue) => issue.restaurantId)).size;
  const categoryTotals = allIssues.reduce((acc, issue) => ({ ...acc, [issue.category]: (acc[issue.category] || 0) + 1 }), {});
  const topCategory = Object.entries(categoryTotals).sort((a, b) => b[1] - a[1])[0];
  const restaurantTotals = allIssues.reduce((acc, issue) => ({ ...acc, [issue.restaurant]: (acc[issue.restaurant] || 0) + 1 }), {});
  const topRestaurant = Object.entries(restaurantTotals).sort((a, b) => b[1] - a[1])[0];
  const pendingUpdates = restaurants.reduce((sum, restaurant) => sum + restaurant.updates.filter((request) => ["new", "in_progress"].includes(request.status)).length, 0);

  const overviewMetrics = [
    { label: ar ? "إجمالي النواقص" : "Total issues", value: allIssues.length, note: ar ? "عبر كل المطاعم" : "Across all restaurants" },
    { label: ar ? "حرجة" : "Critical", value: allIssues.filter((issue) => issue.severity === "danger").length, note: ar ? "تتطلب متابعة فورية" : "Require immediate follow-up" },
    { label: ar ? "تحتاج متابعة" : "Need follow-up", value: allIssues.filter((issue) => issue.severity === "warning").length, note: ar ? "غير عاجلة لكن مطلوبة" : "Not urgent but required" },
    { label: ar ? "مطاعم متأثرة" : "Affected restaurants", value: `${affectedRestaurants} / ${restaurants.length}`, note: ar ? "لديها نقص واحد على الأقل" : "Have at least one issue" },
  ];

  const analyticsMetrics = [
    { label: ar ? "الأكثر تكرراً" : "Most frequent", value: topCategory?.[1] || 0, note: topCategory?.[0] || "—" },
    { label: ar ? "الأكثر احتياجاً للمتابعة" : "Most in need of follow-up", value: topRestaurant?.[1] || 0, note: topRestaurant?.[0] || "—" },
    { label: ar ? "تحديثات قيد المعالجة" : "Updates in progress", value: pendingUpdates, note: ar ? "عبر كل المطاعم" : "Across all restaurants" },
    { label: ar ? "مطاعم بدون أي نقص" : "Restaurants with no issues", value: `${restaurants.length - affectedRestaurants} / ${restaurants.length}`, note: ar ? "مكتملة بالكامل" : "Fully complete" },
  ];

  const columns = [
    { key: "restaurant", label: ar ? "المطعم" : "Restaurant" },
    { key: "restaurantCode", label: ar ? "المعرّف" : "ID" },
    { key: "category", label: ar ? "القسم" : "Category" },
    { key: "detail", label: ar ? "التفصيل" : "Details" },
    {
      key: "severity",
      label: ar ? "الأهمية" : "Severity",
      render: (row) => (
        <Tags variant={row.severity}>
          {row.severity === "danger" ? (ar ? "حرج" : "Critical") : row.severity === "warning" ? (ar ? "يحتاج متابعة" : "Need follow-up") : ar ? "معلومة" : "Information"}
        </Tags>
      ),
    },
    {
      key: "action",
      label: ar ? "الإجراء" : "Action",
      render: (row) => (
        
        <Button  variant="primary" size="sm" appearance="outline" onClick={() => onJump(row.restaurantId, row.categoryKey)}>
          {ar ? "عرض" : "View"}
        </Button>
      ),
    },
  ];

  return (
    <div className="lud-admin">
      <Button icon={<ArrowBigRight size={18} />} appearance="outline" onClick={onBack}>
        {ar ? "رجوع للمطاعم" : "Back to restaurants"}
      </Button>
      <header>
        <h1>{ar ? "نظرة إدارية شاملة" : "Admin Overview"}</h1>
        <p>{ar ? "ملخص النواقص والطلبات المعلّقة عبر جميع المطاعم" : "Summary of missing items and pending requests across all restaurants"}</p>
      </header>
      <MetricsGrid metrics={overviewMetrics} />
      <MetricsGrid metrics={analyticsMetrics} />
      <div className="lud-admin__filters">
        <Select  value={restaurantFilter} onChange={(event) => setRestaurantFilter(event.target.value)} options={[{ value: "all", label: ar ? "كل المطاعم" : "All restaurants" }, ...restaurants.map((restaurant) => ({ value: restaurant.id, label: restaurant.name }))]} />
        <Select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} options={[{ value: "all", label: ar ? "كل الأقسام" : "All categories" }, ...CATEGORIES.map((category) => ({ value: category.key, label: category.label[lang] }))]} />
        <Select value={severityFilter} onChange={(event) => setSeverityFilter(event.target.value)} options={[
          { value: "all", label: ar ? "كل درجات الأهمية" : "All severities" },
          { value: "danger", label: ar ? "حرج" : "Critical" },
          { value: "warning", label: ar ? "يحتاج متابعة" : "Need follow-up" },
          { value: "neutral", label: ar ? "معلومة" : "Information" },
        ]} />
        <Button
  type="button"
  variant="warning"
  appearance="soft"
  className="clear-filters-btn"
  onClick={onClearFilters}
>
  {ar ? "مسح الفلاتر" : "Clear Filters"}
</Button>
      </div>
      <Table columns={columns} data={issues} emptyText={ar ? "لا توجد نواقص مطابقة للفلاتر المختارة." : "No issues match the selected filters."} />
    </div>
  );
}
